import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal, message } from 'antd';
import { FJ730, j218Api } from '../../services/j218Api';
import { useJ218Mode } from './hooks/useJ218Mode';
import { useLookupNames } from './hooks/useLookupNames';
import { useJ218Record } from './hooks/useJ218Record';
import { useJ218Navigation } from './hooks/useJ218Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import PermissionGuard from '../../components/PermissionGuard';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';
import { Modules } from '../../constants/permissions';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2WovenAnalysis from './components/Tab2WovenAnalysis';
import Tab3Product from './components/Tab3Product';
import WashLabelGrid from './components/WashLabelGrid';
import { getStatusBadge, dateToInput } from './helpers';
import './J218Form.css';
import { BatchAcceptanceBridge, pwj218f1AcceptanceManifest } from '../../features/acceptance';

const J218Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.J218);
  const { mode, setMode, isEditing } = useJ218Mode();
  const { names, resolveAllNames, resolveSingleName, clearNames } = useLookupNames();
  const {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, applyDraft,
  } = useJ218Record();

  const [activeTab, setActiveTab] = useState('1');
  const [sourceId, setSourceId] = useState('');

  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result.data);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = useJ218Navigation(loadRecordWithNames);

  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        console.error('Failed to load J218 list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddAction = useCallback(async () => {
    if (isEditing) {
      await handleSave(mode, async (result: FJ730) => {
        if (mode === 'add') {
          const ids = await loadList();
          const newIdx = ids.indexOf(result.fj730_01);
          setCurrentIdx(newIdx >= 0 ? newIdx : 0);
        }
        applyResult(result);
        resolveAllNames(result);
        setMode('browse');
      });
    } else {
      setMode('add');
      resetForAdd();
      clearNames();
    }
  }, [isEditing, mode, handleSave, loadList, setCurrentIdx, applyResult, resolveAllNames, setMode, resetForAdd, clearNames]);

  const handleModifyAction = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      if (record) resolveAllNames(record);
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, revertToRecord, record, resolveAllNames, setMode]);

  const handleDeleteAction = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除分析單 ${record.fj730_01} 嗎？`,
      okText: '確定',
      cancelText: '取消',
      onOk: async () => {
        await handleDelete(async () => {
          const ids = await loadList();
          if (ids.length > 0) {
            setCurrentIdx(0);
            await loadRecordWithNames(ids[0]);
          } else {
            resetForAdd();
          }
          setMode('browse');
        });
      },
    });
  }, [record, handleDelete, loadList, setCurrentIdx, loadRecordWithNames, resetForAdd, setMode]);

  const handleExit = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const statusBadge = getStatusBadge(formData.fj730_80);
  const approvalState = {
    statusCode: String(formData.fj730_80 || ''),
    isApproved: formData.fj730_80 === 'P',
    isPending: formData.fj730_80 === 'S' || formData.fj730_80 === 'D',
    isLocked: formData.fj730_80 === 'P',
  };

  const handlePrint = useCallback(() => {
    message.info('列印功能尚未實作');
  }, []);

  const handleLoadSource = useCallback(async () => {
    const id = sourceId.trim();
    if (!id) {
      message.warning('請先輸入來源分析單號');
      return;
    }
    try {
      const draft = await j218Api.loadSourceDraft(id);
      applyDraft(draft);
      resolveAllNames(draft);
      setMode('add');
      message.success(`已從 ${id} 帶入草稿；尚未儲存`);
    } catch {
      message.error(`找不到來源分析單 ${id}`);
    }
  }, [sourceId, applyDraft, resolveAllNames, setMode]);

  return (
    <PermissionGuard module={Modules.J218}>
      <div className="j218-form">
        <div className="page-wrapper">
          <div className="form-header">
            <h2>新產品開發分析（單層梭織）</h2>
            {record && (
              <span className="status-badge" style={{ background: statusBadge.color }}>
                {statusBadge.text}
              </span>
            )}
          </div>

          {/* Form-level fields — always visible above tabs (FoxPro form-level) */}
          <div className="form-level-fields">
            <label>分析單號</label>
            <input data-contract-field="fj730_01" value={String(getField('fj730_01') || '')} disabled className="readonly-field" />
            <label>入單日期</label>
            <input type="date" value={dateToInput(getField('fj730_12') as string)} disabled className="readonly-field" />
            <Input
              aria-label="來源分析單號"
              placeholder="來源分析單號"
              value={sourceId}
              onChange={(event) => setSourceId(event.target.value)}
              disabled={isEditing}
              style={{ width: 160 }}
            />
            <Button onClick={handleLoadSource} disabled={isEditing || !perms.canCreate}>
              從來源單帶入
            </Button>
          </div>

          <div className="tab-bar">
            <button className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')}>
              基本資料
            </button>
            <button className={activeTab === '2' ? 'active' : ''} onClick={() => setActiveTab('2')}>
              單層梭織
            </button>
            <button className={activeTab === '3' ? 'active' : ''} onClick={() => setActiveTab('3')}>
              成品/物化性檢測
            </button>
          </div>

          {activeTab === '1' && (
            <>
              <Tab1BasicInfo
                formData={formData}
                names={names}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
              />
              <WashLabelGrid
                details={details}
                setDetails={setDetails}
                isEditing={isEditing}
                addDetailRow={addDetailRow}
                deleteDetailRow={deleteDetailRow}
              />
            </>
          )}

          {activeTab === '2' && (
            <Tab2WovenAnalysis getField={getField} setField={setField} isEditing={isEditing} />
          )}

          {activeTab === '3' && (
            <Tab3Product getField={getField} setField={setField} isEditing={isEditing} />
          )}
        </div>

        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!record}
          currentIdx={currentIdx}
          recordCount={allIds.length}
          onNavFirst={() => navTo(0)}
          onNavPrev={() => navTo(currentIdx - 1)}
          onNavNext={() => navTo(currentIdx + 1)}
          onNavLast={() => navTo(allIds.length - 1)}
          onAdd={handleAddAction}
          onSave={handleAddAction}
          onModify={handleModifyAction}
          onCancel={handleModifyAction}
          onDelete={handleDeleteAction}
          onPrint={handlePrint}
          onExit={handleExit}
          approval={approvalState}
          canView={perms.canView}
          canCreate={perms.canCreate}
          canEdit={perms.canEdit}
          canDelete={perms.canDelete}
          canPrint={perms.canView}
          acceptanceActionIds={{
            create: 'PWJ218-ACT-CREATE',
            update: 'PWJ218-ACT-UPDATE',
            delete: 'PWJ218-ACT-DELETE',
          }}
        />
        <BatchAcceptanceBridge
          manifest={pwj218f1AcceptanceManifest}
          records={record ? [record] : []}
          selectedKeys={record?.fj730_01 ? [record.fj730_01] : []}
          keyOf={(item) => item.fj730_01}
          queryExact={(key) => j218Api.get(key)}
          lastApiResponse={record}
          loading={loading}
          onAutoFill={() => allIds[0] && loadRecordWithNames(allIds[0])}
          autoFillDisabled={allIds.length === 0}
          screenDraft={{ master: formData, details }}
        />
      </div>
    </PermissionGuard>
  );
};

export default J218Form;
